import { useEffect } from 'react';
import posthog, { isPostHogEnabled } from './posthog';
import { getChartIdentity } from './chartIdentity';
import { getToolEntitlement } from './toolTier';
import { getChartsAttributionContext } from './chartsAttribution';
import { getPlanTier, getSubscriptionStatus, PLAN_TIER_BY_USER_TIER } from './userProperties';
import { resolveToolId } from './toolCatalog';
import { getAnalyticsUserSnapshot } from './chartIdentity';
import { TIERS } from '../common/utility/roleConstants';
import packageJson from '../../package.json';

export const CHARTS_ACCESS_PAYWALL = 'paywall_shown';
export const CHARTS_ACCESS_DENIED = 'denied';

const CHARTS_ACCESS_OPENED = 'opened';
const CHARTS_ACCESS_FAILED = 'failed';
const CHARTS_TOOL_CLICKED = 'charts_tool_clicked';
const CHARTS_TOOL_OPENED = 'charts_tool_opened';
const CHARTS_TOOL_SESSION_STARTED = 'charts_tool_session_started';
const CHARTS_TOOL_SESSION_HEARTBEAT = 'charts_tool_session_heartbeat';
const CHARTS_TOOL_SESSION_ENDED = 'charts_tool_session_ended';
const CHARTS_TOOL_OPEN_KEY = 'ph_charts_tool_open';
const CHARTS_TOOL_OPEN_TAB_KEY = 'ph_charts_tool_open_tab';
const CHARTS_TOOL_OPEN_ORIGIN_KEY = 'ph_charts_tool_open_origin';
const CHARTS_TOOL_OPENED_DEST_KEY = 'ph_charts_tool_opened_dest';
const TOOL_HEARTBEAT_MS = 30000;
const TOOL_IDLE_MS = 5 * 60 * 1000;

const CHART_SECTION_IDS = new Set([TIERS.FREE, TIERS.PREMIUM, TIERS.ELITE]);

const SYMBOL_NAMES = new Set([
  'NIFTY',
  'BANKNIFTY',
  'FINNIFTY',
  'MIDCPNIFTY',
  'SENSEX',
]);

function splitRoute(route) {
  const url = new URL(route || '/', 'https://charts.vtrender.com');
  return {
    pathname: url.pathname,
    search: url.search,
  };
}

function destinationMatches(destination) {
  if (typeof window === 'undefined' || !destination) {
    return false;
  }
  const target = splitRoute(destination);
  if (target.pathname !== window.location.pathname) {
    return false;
  }
  const current = new URLSearchParams(window.location.search);
  const expected = new URLSearchParams(target.search);
  for (const [key, value] of expected.entries()) {
    if (current.get(key) !== value) {
      return false;
    }
  }
  return true;
}

function toolIdFromRoute(route) {
  return resolveToolId(route);
}

function clearOpenedDestination() {
  if (typeof window === 'undefined') {
    return;
  }
  removeKey(window.sessionStorage, CHARTS_TOOL_OPENED_DEST_KEY);
}

function hasOpenedDestination(destination_path) {
  if (typeof window === 'undefined') {
    return false;
  }
  try {
    return window.sessionStorage.getItem(CHARTS_TOOL_OPENED_DEST_KEY) === destination_path;
  } catch {
    return false;
  }
}

function markOpenedDestination(destination_path) {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    window.sessionStorage.setItem(CHARTS_TOOL_OPENED_DEST_KEY, destination_path);
  } catch {
    // Ignore storage failures.
  }
}

function buildLocationToolProperties() {
  if (typeof window === 'undefined') {
    return {};
  }
  const destination_path = `${window.location.pathname}${window.location.search}`;
  const { pathname, search } = splitRoute(destination_path);
  const identity = getChartIdentity(pathname, search);
  const user = getAnalyticsUserSnapshot();

  return {
    tool_id: toolIdFromRoute(destination_path),
    tool_name: identity?.chart_name,
    tool_category: identity?.chart_type,
    required_tier: getToolEntitlement(destination_path),
    plan_tier: user.plan_tier,
    subscription_status: user.subscription_status,
    ...(identity?.symbol ? { instrument: identity.symbol, symbol: identity.symbol } : {}),
    source_path: destination_path,
    destination_path,
  };
}

function toolName(item, parentItem) {
  if (parentItem?.name && SYMBOL_NAMES.has(String(item?.name || '').toUpperCase())) {
    return parentItem.name;
  }
  if (parentItem?.name && item?.name) {
    return `${parentItem.name} ${item.name}`;
  }
  return item?.name;
}

function readJson(storage, key) {
  try {
    const raw = storage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function writeJson(storage, key, value) {
  try {
    storage.setItem(key, JSON.stringify(value));
  } catch {
    // Storage can be unavailable in private mode.
  }
}

function removeKey(storage, key) {
  try {
    storage.removeItem(key);
  } catch {
    // Ignore storage failures.
  }
}

function capture(eventName, properties, options) {
  if (!isPostHogEnabled || !posthog?.capture) {
    return;
  }
  const sessionId = posthog.get_session_id?.();
  const attribution = getChartsAttributionContext();
  posthog.capture(eventName, {
    ...properties,
    app_version: packageJson.version,
    ...(sessionId ? { session_id: sessionId } : {}),
    ...(attribution.handoff_id ? { handoff_id: attribution.handoff_id } : {}),
  }, options);
}

function withCurrentSymbol(properties) {
  if (typeof window === 'undefined') {
    return properties;
  }
  const identity = getChartIdentity(window.location.pathname, window.location.search);
  if (!identity?.symbol) {
    return properties;
  }
  return {
    ...properties,
    instrument: properties.instrument || identity.symbol,
    symbol: properties.symbol || identity.symbol,
  };
}

function buildProperties({ auth, item, parentItem, section, menuPosition }) {
  const route = item?.route || '';
  const { pathname, search } = splitRoute(route);
  const identity = getChartIdentity(pathname, search);
  const symbol = identity?.symbol;
  const planTier = getPlanTier(auth);

  return {
    tool_id: toolIdFromRoute(route),
    tool_name: toolName(item, parentItem),
    tool_category: identity?.chart_type || parentItem?.name || item?.name,
    required_tier: getToolEntitlement(route) || PLAN_TIER_BY_USER_TIER[section?.id],
    plan_tier: planTier,
    subscription_status: getSubscriptionStatus(auth, planTier),
    ...(symbol ? { instrument: symbol, symbol } : {}),
    source_path: typeof window === 'undefined'
      ? '/'
      : `${window.location.pathname}${window.location.search}`,
    destination_path: route,
    menu_section: section?.title || section?.id,
    menu_position: menuPosition,
  };
}

function rememberOpen(properties, openInNewTab) {
  const record = {
    id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
    startedAt: Date.now(),
    destination_path: properties.destination_path,
    properties,
  };

  if (typeof window === 'undefined') {
    return;
  }

  if (openInNewTab) {
    writeJson(localStorage, CHARTS_TOOL_OPEN_TAB_KEY, record);
    try {
      window.sessionStorage.setItem(CHARTS_TOOL_OPEN_ORIGIN_KEY, record.id);
    } catch {
      // The new tab can still claim the record.
    }
    return;
  }

  writeJson(window.sessionStorage, CHARTS_TOOL_OPEN_KEY, record);
  removeKey(localStorage, CHARTS_TOOL_OPEN_TAB_KEY);
}

function clearPending() {
  if (typeof window === 'undefined') {
    return;
  }
  removeKey(window.sessionStorage, CHARTS_TOOL_OPEN_KEY);
  removeKey(localStorage, CHARTS_TOOL_OPEN_TAB_KEY);
}

function takePending() {
  if (typeof window === 'undefined') {
    return null;
  }

  const sessionRecord = readJson(window.sessionStorage, CHARTS_TOOL_OPEN_KEY);
  if (sessionRecord && destinationMatches(sessionRecord.destination_path)) {
    removeKey(window.sessionStorage, CHARTS_TOOL_OPEN_KEY);
    return sessionRecord;
  }

  const tabRecord = readJson(localStorage, CHARTS_TOOL_OPEN_TAB_KEY);
  if (!tabRecord || !destinationMatches(tabRecord.destination_path)) {
    return null;
  }

  let originId;
  try {
    originId = window.sessionStorage.getItem(CHARTS_TOOL_OPEN_ORIGIN_KEY);
  } catch {
    originId = null;
  }
  if (originId && originId === tabRecord.id) {
    return null;
  }

  removeKey(localStorage, CHARTS_TOOL_OPEN_TAB_KEY);
  return tabRecord;
}

export function captureChartsMenuSelection({
  auth,
  item,
  parentItem,
  section,
  menuPosition,
  accessResult,
  openInNewTab = false,
} = {}) {
  if (!CHART_SECTION_IDS.has(section?.id) || !item?.route) {
    return;
  }

  const properties = buildProperties({
    auth,
    item,
    parentItem,
    section,
    menuPosition,
  });

  if (accessResult) {
    clearPending();
    capture(CHARTS_TOOL_CLICKED, {
      ...properties,
      access_result: accessResult,
      load_time_ms: 0,
    });
    return;
  }

  endToolSession('tool_changed');
  clearOpenedDestination();
  rememberOpen(properties, openInNewTab);
  capture(CHARTS_TOOL_CLICKED, properties);
}

let toolSession;
let heartbeatTimer;
let timedOutPath;

function markToolActivity() {
  if (toolSession) {
    toolSession.lastActivity = Date.now();
    return;
  }
  if (timedOutPath && destinationMatches(timedOutPath)) {
    beginToolSession();
  }
}

function toolIsActive() {
  return Boolean(
    toolSession
    && document.visibilityState === 'visible'
    && document.hasFocus()
    && Date.now() - toolSession.lastActivity < TOOL_IDLE_MS
  );
}

function accumulateActive() {
  if (!toolSession) {
    return;
  }
  const now = Date.now();
  if (toolIsActive()) {
    toolSession.activeMs += now - toolSession.lastTick;
  }
  toolSession.lastTick = now;
}

function toolSessionProps(extra) {
  return {
    tool_id: toolSession.tool_id,
    tool_name: toolSession.tool_name,
    plan_tier: toolSession.plan_tier,
    instrument: toolSession.instrument,
    symbol: toolSession.symbol,
    tool_session_id: toolSession.tool_session_id,
    active_seconds: Math.floor(toolSession.activeMs / 1000),
    ...extra,
  };
}

function endToolSession(end_reason, instantly) {
  if (!toolSession) {
    return;
  }
  accumulateActive();
  timedOutPath = end_reason === 'session_timeout' ? toolSession.destination_path : undefined;
  capture(
    CHARTS_TOOL_SESSION_ENDED,
    toolSessionProps({ end_reason }),
    instantly ? { transport: 'sendBeacon', send_instantly: true } : undefined
  );
  toolSession = undefined;
  if (heartbeatTimer) {
    clearInterval(heartbeatTimer);
    heartbeatTimer = undefined;
  }
}

function beginToolSession(fromClick) {
  if (typeof window === 'undefined' || !isPostHogEnabled) {
    return;
  }
  const destination_path = `${window.location.pathname}${window.location.search}`;
  if (toolSession && destinationMatches(toolSession.destination_path)) {
    return;
  }
  endToolSession('tool_changed');
  clearOpenedDestination();

  const identity = getChartIdentity(window.location.pathname, window.location.search);
  const now = Date.now();
  toolSession = {
    destination_path,
    tool_id: fromClick?.tool_id || toolIdFromRoute(destination_path),
    tool_name: fromClick?.tool_name || identity?.chart_name,
    plan_tier: fromClick?.plan_tier,
    instrument: fromClick?.instrument || identity?.symbol,
    symbol: fromClick?.symbol || identity?.symbol,
    tool_session_id: `${now.toString(36)}-${Math.random().toString(36).slice(2, 8)}`,
    activeMs: 0,
    lastTick: now,
    lastActivity: now,
  };
  capture(CHARTS_TOOL_SESSION_STARTED, toolSessionProps({ active_seconds: 0 }));
  heartbeatTimer = setInterval(heartbeatToolSession, TOOL_HEARTBEAT_MS);
}

function heartbeatToolSession() {
  if (!toolSession) {
    return;
  }
  accumulateActive();
  if (!destinationMatches(toolSession.destination_path)) {
    endToolSession('tool_changed');
    return;
  }
  if (Date.now() - toolSession.lastActivity >= TOOL_IDLE_MS) {
    endToolSession('session_timeout');
    return;
  }
  capture(CHARTS_TOOL_SESSION_HEARTBEAT, toolSessionProps());
}

export function endChartsToolSession(end_reason) {
  endToolSession(end_reason, true);
}

if (typeof window !== 'undefined') {
  document.addEventListener('visibilitychange', accumulateActive);
  window.addEventListener('focus', markToolActivity);
  window.addEventListener('blur', accumulateActive);
  window.addEventListener('pointerdown', markToolActivity, { passive: true });
  window.addEventListener('keydown', markToolActivity);
  window.addEventListener('wheel', markToolActivity, { passive: true });
  window.addEventListener('pagehide', () => endToolSession('page_unloaded', true));
}

function notifyChartsToolReady() {
  const record = takePending();
  const load_time_ms = record ? Math.max(0, Date.now() - record.startedAt) : 0;
  const destination_path = typeof window !== 'undefined'
    ? `${window.location.pathname}${window.location.search}`
    : '';
  const openProperties = withCurrentSymbol(record?.properties || buildLocationToolProperties());

  if (destination_path && !hasOpenedDestination(destination_path)) {
    capture(CHARTS_TOOL_OPENED, {
      ...openProperties,
      access_result: CHARTS_ACCESS_OPENED,
      load_time_ms,
    });
    markOpenedDestination(destination_path);
  }
  captureChartInteraction('charts_tool_load_succeeded', {
    ...(record?.properties || openProperties),
    load_time_ms,
  });
  beginToolSession(record?.properties || openProperties);
}

function notifyChartsToolFailed() {
  const record = takePending();
  const load_time_ms = record ? Math.max(0, Date.now() - record.startedAt) : 0;
  if (record) {
    capture(CHARTS_TOOL_CLICKED, {
      ...withCurrentSymbol(record.properties),
      access_result: CHARTS_ACCESS_FAILED,
      load_time_ms,
    });
  }
  const failProps = {
    ...(record?.properties || {}),
    load_time_ms,
  };
  captureChartInteraction('charts_tool_load_failed', {
    ...failProps,
    error_category: 'tool_load',
    error_code: 'load_failed',
  });
}

export function useChartsToolOpenState(ready, failed = false) {
  useEffect(() => {
    if (ready) {
      notifyChartsToolReady();
      return;
    }
    if (failed) {
      notifyChartsToolFailed();
    }
  }, [ready, failed]);
}

const SHORT_SYMBOL = {
  NF: 'NIFTY',
  BN: 'BANKNIFTY',
  FN: 'FINNIFTY',
  MN: 'MIDCPNIFTY',
  SNX: 'SENSEX',
};

function underlierOf(value) {
  if (!value) {
    return undefined;
  }
  const raw = String(value).toUpperCase();
  for (const name of SYMBOL_NAMES) {
    if (raw === name || raw.startsWith(`${name}_`)) {
      return name;
    }
  }
  const token = raw.replace(/^O_/, '').split(/[_-\s]/)[0];
  return SHORT_SYMBOL[token] || token || undefined;
}

export function captureChartInteraction(eventName, extra = {}) {
  if (typeof window === 'undefined') {
    return;
  }
  const path = `${window.location.pathname}${window.location.search}`;
  const identity = getChartIdentity(window.location.pathname, window.location.search);
  capture(eventName, withCurrentSymbol({
    tool_id: toolSession?.tool_id || toolIdFromRoute(path),
    tool_name: toolSession?.tool_name || identity?.chart_name,
    plan_tier: toolSession?.plan_tier,
    instrument: extra.instrument || identity?.symbol,
    symbol: extra.symbol || identity?.symbol,
    ...extra,
  }));
}

export function captureInstrumentSelection(previousInstrument, nextInstrument) {
  if (!nextInstrument || previousInstrument === nextInstrument) {
    return;
  }
  const previousSymbol = underlierOf(previousInstrument);
  const nextSymbol = underlierOf(nextInstrument);
  captureChartInteraction('charts_instrument_changed', {
    instrument: nextInstrument,
    previous_instrument: previousInstrument,
    symbol: nextSymbol,
  });
  if (previousSymbol && nextSymbol && previousSymbol !== nextSymbol) {
    captureChartInteraction('charts_symbol_changed', {
      symbol: nextSymbol,
      previous_symbol: previousSymbol,
      instrument: nextInstrument,
    });
  }
}
